'use client';

import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { LinkPreview } from 'shared/src/types/link-preview.type';
import {
  fetchLinkPreview,
  getLinkPreviewSnapshot,
  primeLinkPreview,
  subscribeLinkPreview,
  type LinkPreviewSnapshot,
} from './link-preview-cache';

const SERVER_SNAPSHOT: LinkPreviewSnapshot = { status: 'idle', preview: null };

/**
 * The preview of a link, live. `snapshot` seeds it (a resumed conversation);
 * `fetchWith` asks the server when nothing is known yet (a card whose chip
 * never existed in this tab, e.g. after a reload).
 */
export function useLinkPreview(
  url: string | null | undefined,
  opts: { snapshot?: LinkPreview | null; fetchWith?: { locale: string; path?: string; headers?: Record<string, string> } } = {},
): LinkPreviewSnapshot {
  const { snapshot, fetchWith } = opts;
  const state = useSyncExternalStore(
    (listener) => subscribeLinkPreview(url, listener),
    () => getLinkPreviewSnapshot(url),
    () => SERVER_SNAPSHOT,
  );
  // A known preview (a resumed conversation) is shown at once and stored
  // after render: priming during render would notify other subscribers mid-render.
  useEffect(() => {
    if (url && snapshot) primeLinkPreview(url, snapshot);
  }, [url, snapshot]);
  const seeded = useMemo<LinkPreviewSnapshot | null>(
    () => (snapshot ? { status: 'ready', preview: snapshot } : null),
    [snapshot],
  );
  const shouldFetch = Boolean(url && fetchWith && !snapshot && state.status === 'idle');
  const locale = fetchWith?.locale;
  const path = fetchWith?.path;
  useEffect(() => {
    if (!shouldFetch || !url || !locale) return;
    void fetchLinkPreview(url, { locale, path, headers: fetchWith?.headers });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, url, locale, path]);
  return state.status !== 'ready' && seeded ? seeded : state;
}
